// =============================================================================
// VerifiedAddressContext  —  shared property address state
// =============================================================================
// This context holds the property address the user has verified via
// NewCmaModal. It is the single source of truth for the selected address
// across the entire CMA creation flow.
//
// ── WHY IT EXISTS ─────────────────────────────────────────────────────────────
//   The verified address needs to survive navigation between pages:
//     NewCmaModal (verify) → /new-cma (build CMA) → /cma-results (results)
//   Using context + sessionStorage achieves this without prop-drilling and
//   without losing state on a browser refresh.
//
// ── STORAGE ──────────────────────────────────────────────────────────────────
//   The address is persisted to sessionStorage under the key
//   "cma_verified_address_v1". It is cleared when:
//     - The user explicitly clears it (clearVerifiedAddress)
//     - The browser tab is closed (sessionStorage is tab-scoped)
//
// ── BACKWARDS COMPATIBILITY ──────────────────────────────────────────────────
//   The context accepts both a plain string (legacy) and a structured object.
//   normalizeVerifiedAddress() always converts to the structured object shape
//   before storing, so consumers always get a consistent type.
//
// ── SETUP ────────────────────────────────────────────────────────────────────
//   VerifiedAddressProvider is mounted once in src/main.tsx, wrapping the
//   entire app so any page can access it via useVerifiedAddress().
//
// ── CONSUMERS ────────────────────────────────────────────────────────────────
//   src/pages/new-cma.tsx                → reads + clears the address
//   src/components/NewCmaModal.tsx        → writes the address after verification
//   src/sections/dashboard/DashboardView → reads for the "quick start" CMA card
//
// ── EXPORTED API ─────────────────────────────────────────────────────────────
//   verifiedAddress      — the current normalized address object (or null)
//   setVerifiedAddress() — store a new address (string or object both accepted)
//   clearVerifiedAddress()— reset to null (used on "Change Address" / fresh start)
//   useVerifiedAddress() — hook; throws if called outside the provider
// =============================================================================

import React, { useMemo, useState, useEffect, useContext, createContext } from "react";

export type VerifiedAddressValue =
  | string
  | {
      verifiedAddress: string;
      county?: string;
      lat?: number;
      lng?: number;
      [k: string]: any;
    }
  | null;

export type VerifiedAddressDetails =
  | {
      verifiedAddress: string;
      county?: string;
      lat?: number;
      lng?: number;
      [k: string]: any;
    }
  | null;

export type VerifiedAddressContextType = {
  verifiedAddress: VerifiedAddressDetails;
  setVerifiedAddress: (address: VerifiedAddressValue) => void;
  clearVerifiedAddress: () => void;
};

const STORAGE_KEY = "cma_verified_address_v1";

function normalizeVerifiedAddress(v: VerifiedAddressValue): VerifiedAddressDetails {
  if (!v) return null;
  if (typeof v === "string") return { verifiedAddress: v };
  if (typeof v === "object" && typeof (v as any).verifiedAddress === "string") return v as any;
  return null;
}

const VerifiedAddressContext = createContext<VerifiedAddressContextType | undefined>(undefined);

export const VerifiedAddressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [raw, setRaw] = useState<VerifiedAddressValue>(null);

  const verifiedAddress = useMemo(() => normalizeVerifiedAddress(raw), [raw]);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      setRaw(parsed);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      if (!raw) {
        sessionStorage.removeItem(STORAGE_KEY);
      } else {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
      }
    } catch {
      // ignore
    }
  }, [raw]);

  const setVerifiedAddress = (address: VerifiedAddressValue) => {
    setRaw(address);
  };

  const clearVerifiedAddress = () => setRaw(null);

  return (
    <VerifiedAddressContext.Provider value={{ verifiedAddress, setVerifiedAddress, clearVerifiedAddress }}>
      {children}
    </VerifiedAddressContext.Provider>
  );
};

export function useVerifiedAddress() {
  const ctx = useContext(VerifiedAddressContext);
  if (!ctx) throw new Error("useVerifiedAddress must be used within a VerifiedAddressProvider");
  return ctx;
}
